import type { BackfillSummary } from '../types'
import { CATEGORY_DISPLAY_NAMES } from '../types'
import { exportToExcel } from '@/shared/utils/exportToExcel'

interface BackfillSummaryStepProps {
  summary: BackfillSummary
  category: string
  onFinish: () => void
}

export function BackfillSummaryStep({ summary, category, onFinish }: BackfillSummaryStepProps) {
  const handleExport = () => {
    const rows = summary.logs.flatMap((log) =>
      log.results.map((result) => ({
        clientId: log.clientId,
        clientName: log.clientName,
        clientRut: log.clientRut,
        ruleId: result.rule_id,
        ruleName: result.rule_name,
        status: result.status === 'granted' ? 'Asignado' : result.status === 'skipped' ? 'Saltado' : 'Error',
        reason: result.reason ?? '',
        benefitId: result.client_benefit_id ?? '',
        timestamp: log.timestamp,
      })),
    )
    exportToExcel(
      rows,
      `backfill_${category}_${new Date().toISOString().slice(0, 10)}`,
      [
        { key: 'clientId', label: 'Cliente ID' },
        { key: 'clientName', label: 'Nombre' },
        { key: 'clientRut', label: 'RUT' },
        { key: 'ruleId', label: 'Regla ID' },
        { key: 'ruleName', label: 'Regla' },
        { key: 'status', label: 'Estado' },
        { key: 'reason', label: 'Motivo' },
        { key: 'benefitId', label: 'Benefit ID' },
        { key: 'timestamp', label: 'Timestamp' },
      ],
    )
  }

  const hasErrors = summary.totalErrors > 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`rounded-lg p-6 ${hasErrors ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
        <h3 className={`text-xl font-bold ${hasErrors ? 'text-amber-800' : 'text-green-800'}`}>
          {hasErrors ? 'Proceso completado con errores' : 'Proceso completado exitosamente'}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Asignación retroactiva para nivel {CATEGORY_DISPLAY_NAMES[category]}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-gray-900">
            {summary.totalProcessed}
          </p>
          <p className="text-sm text-gray-500 mt-1">Clientes procesados</p>
        </div>
        <div className="bg-white border rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-green-600">
            {summary.totalGranted}
          </p>
          <p className="text-sm text-gray-500 mt-1">Beneficios asignados</p>
        </div>
        <div className="bg-white border rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-gray-400">
            {summary.totalSkipped}
          </p>
          <p className="text-sm text-gray-500 mt-1">Saltados</p>
        </div>
        <div className="bg-white border rounded-lg p-4 text-center">
          <p className={`text-3xl font-bold ${summary.totalErrors > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {summary.totalErrors}
          </p>
          <p className="text-sm text-gray-500 mt-1">Errores</p>
        </div>
      </div>

      {/* Audit trail info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="text-sm font-medium text-blue-800">Auditoría</p>
          <p className="text-sm text-blue-700 mt-0.5">
            Cada beneficio asignado quedo marcado con <code className="bg-blue-100 px-1 rounded">backfill: true</code> en
            sus metadatos. Puedes consultar en la base de datos todos los beneficios retroactivos
            filtrando por este campo.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <button onClick={handleExport} className="btn btn-secondary">
          <svg className="w-4 h-4 mr-1.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Exportar log (Excel)
        </button>
        <button onClick={onFinish} className="btn btn-primary">
          Volver al dashboard
        </button>
      </div>
    </div>
  )
}
